import {Box, CircularProgress, Drawer, IconButton, Paper, Tooltip, Typography} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import HomeMadeDialog from "../dialogs/HomeMadeDialog";
import {msg} from "@lingui/macro";
import {useEffect, useRef, useState} from "react";
import {useLingui} from "@lingui/react";
import DeleteIcon from "@mui/icons-material/Delete";
import httpReq from "../../services/httpReq";
import {Add} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";

const ReportAttachmentsList = ({isOpen, setIsOpen, selectedVideoReport}) => {

    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
    const {_} = useLingui()
    const [reportAttachments, setReportAttachments] = useState([]);
    const [isLoadingSave, setIsLoadingSave] = useState(false);
    const [attachmentToDelete,setAttachmentToDelete] = useState(null);
    const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if(!isLoadingSave) {
            getReportAttachments()
        }
    }, [selectedVideoReport,isLoadingSave]);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const splitFileType = file.type.split("/");
        setIsLoadingSave(true);

        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result.split(",")[1];

            const body = {
                title: file.name,
                description: file.description || "",
                fileName: file.name,
                fileExtension: splitFileType[1],
                fileMimeType: file.type,
                fileContent: base64String,
                reportFk: selectedVideoReport.id
            };
            httpReq("ReportAttachment/Insert", "POST", body, true).then((res) => {
                setIsLoadingSave(false);
            })
        };

        reader.readAsDataURL(file);
    };

    const getReportAttachments = async () => {
        const res = await httpReq('ReportAttachment/GetList/' + selectedVideoReport.id, 'GET', null, true);
        if (!res || res.responseAnyError) return;
        setReportAttachments(res.responseData);
    }

    const downloadAttachment = async (attachment) => {
        try {
            const res = await httpReq('ReportAttachment/Get/' + attachment.id, 'GET', null, true);
            if (!res || res.responseAnyError) return;

            const base64String = res.responseData.fileContent;
            const mimeType = attachment.fileMimeType || 'application/octet-stream';
            const fileName = attachment.fileName || 'file';

            //
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            const blob = new Blob([byteArray], { type: mimeType });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            //
            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Download failed', error);
        }
    };


    const deleteReportAttachment = async () => {
        setDeletingAttachmentId(attachmentToDelete.id);
        const res = await httpReq('ReportAttachment/Delete/' + attachmentToDelete.id, 'DELETE', null, true);
        if (!res || res.responseAnyError) {
            setDeletingAttachmentId(null);
            return;
        }
        setIsOpenDeleteDialog(false);
        setAttachmentToDelete(null);
        setDeletingAttachmentId(null);
        await getReportAttachments();
    }

    return (
        <Drawer
            transitionDuration={400}
            anchor="right"
            open={isOpen}
            onClose={() => setIsOpen(false)}
        >
            <Box
                sx={{
                    width: 400,
                    height: '100%',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    backgroundColor: '#f5f5f5',
                }}
            >
                {/* HEADER */}
                <Box
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6">
                        <ListAltIcon sx={{verticalAlign: 'middle', mr: 1}}/>
                        Attachments List
                    </Typography>
                    <Typography variant="subtitle2">{selectedVideoReport?.title}</Typography>
                </Box>

                <Box sx={{overflowY: 'auto', display: 'flex', height: '100%', flexDirection: 'column', gap: 1}}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {isLoadingSave ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} color="primary" />
                                <Typography variant="caption" color="textSecondary">
                                    Caricamento allegato...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                <IconButton
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'primary.dark'
                                        }
                                    }}
                                    size="small"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Add fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {reportAttachments.map((attachment, key) => (
                        <Paper
                            key={key}
                            elevation={3}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: key % 2 === 0 ? 'white' : '#fafafa',
                                animation: `fadeInUp 0.3s ease-out ${key * 0.05}s`,
                                animationFillMode: 'forwards',
                                opacity: 0,
                                transition: 'transform 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <Box sx={{flex: 2}}>
                                <Typography variant="body2" color="text.primary">
                                    {attachment.title || attachment.fileName}
                                </Typography>

                            </Box>

                            <Box sx={{flex: 1, display: 'flex', justifyContent: 'flex-end'}}>
                                <Tooltip title="Download Attachment">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                            downloadAttachment(attachment)
                                        }}
                                    >
                                        <DownloadIcon/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete Attachment">
                                    {deletingAttachmentId === attachment.id ? (
                                        <CircularProgress size={20} color="error" />
                                    ) : (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setAttachmentToDelete(attachment);
                                                setIsOpenDeleteDialog(true);
                                            }}
                                        >
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    )}
                                </Tooltip>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {isOpenDeleteDialog && (
                    <HomeMadeDialog
                        isOpen={isOpenDeleteDialog}
                        labelClose={'Cancel'}
                        labelConfirm={'Confirm'}
                        text={_(msg`Are you sure?`)}
                        fnClose={() => {
                            setAttachmentToDelete(null)
                            setIsOpenDeleteDialog(false)
                        }}
                        fnConfirmAction={() => deleteReportAttachment(attachmentToDelete)}
                        title={`Delete attachment`}
                    />
                )}
            </Box>
        </Drawer>
    )
}

export default ReportAttachmentsList;
